import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import STForumCategory from "../../assets/styles/organisms/forumCategory.module.scss";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import NavigateToCreateBoardButton from "../atoms/NavigateToCreateBoardButton";
import ForumCategoryHeader from "./ForumCategoryHeader";
import { fetchForumCategoriesDetail } from "../../utils/api/fetchForumCategoryDetail";
import Pagenation from "../molecules/AppPagenation";
import { routes } from "../../router/Router";
import { fetchBoardsByCategoryId } from "../../utils/api/fetchBoardsByCategoryId";
import { CheckSpFlag } from "../../hooks/CheckSpFlag";
import { MdKeyboardDoubleArrowLeft } from "react-icons/md";
import BoardCard from "../molecules/BoardCard";
import ForumLoadingPage from "../atoms/MessageBoardLoadingPage";

const LIMIT = 10;
const ForumCategoryBoardList = () => {
  const params = useParams<{ category_id?: string }>();
  const { category_id = 0 } = params ?? {};
  const query = new URLSearchParams(useLocation().search);
  const page = query.get("page") ? Number(query.get("page")) : 1;
  const [boards, setBoards] = useState<any[]>([]);
  const [totalBoards, setTotalBoards] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(page);
  const history = useHistory();
  const spFlag = CheckSpFlag();

  useEffect(() => {
    const getForumCategoryBoardList = async () => {
      setIsLoading(true);
      try {
        const response = await fetchBoardsByCategoryId(
          Number(category_id),
          LIMIT,
          page
        );

        if (response.isSuccess) {
          const result = response?.result ?? {};
          const { boards = [], total = 0 } = (result as any) ?? {};
          if (Array.isArray(boards) && boards.length !== 0) {
            setBoards(boards);
            setTotalBoards(total);
          }
        } else {
          throw new Error("");
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    getForumCategoryBoardList();
  }, [category_id, page]);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const loadMore = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (
        entries[0].isIntersecting &&
        boards.length < totalBoards &&
        !isLoading
      ) {
        setIsLoading(true);

        timerRef.current = setTimeout(() => {
          const newPage = currentPage + 1;

          fetchBoardsByCategoryId(Number(category_id), LIMIT, newPage).then(
            (res: any) => {
              if (!res.isSuccess || !res.result)
                return history.push(routes.error.path);
              const newBoards: any[] = res.result.boards;
              setBoards((prevBoards) => [...prevBoards, ...newBoards]);
              setCurrentPage((pre) => pre + 1);
              setIsLoading(false);
              if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
              }
            }
          );
        }, 500);
      }
    },
    [boards.length, category_id, currentPage, history, isLoading, totalBoards]
  );

  const observer = useMemo(
    () =>
      new IntersectionObserver(loadMore, {
        rootMargin: "100px",
      }),
    [loadMore]
  );

  useEffect(() => {
    const target = document.getElementById("scrollTarget");
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [observer]);

  if ((isLoading && !spFlag) || (isLoading && boards.length === 0)) {
    return <ForumLoadingPage className={STForumCategory.boardListWrapper} />;
  }

  if (!Array.isArray(boards) || boards.length === 0) {
    return <div className={STForumCategory.noData}>投稿はまだありません</div>;
  }
  return (
    <div className={STForumCategory.boardListWrapper}>
      <div className={STForumCategory.boardList}>
        {boards.map((board: any) => {
          const {
            id,
            avatar_url: avatarUrl = "",
            nickname,
            title,
            content,
            created_at: createdAt,
            image_urls: imageUrls,
            like_flag: likeFlag,
            number_of_comments: commentCount,
            comment_flag: commentFlag,
            number_of_likes: likeCount,
            board_category_id: categoryId,
            uid,
          } = board;
          const boardDetail = {
            id,
            avatarUrl,
            nickname,
            title,
            content,
            createdAt,
            imageUrls,
            likeFlag,
            commentCount,
            likeCount,
            categoryId,
            uid,
            commentFlag,
          };
          return (
            <BoardCard
              boardDetail={boardDetail}
              className={"customBoardListCategoryPage"}
            />
          );
        })}
      </div>
      {totalBoards > LIMIT && !spFlag && (
        <Pagenation
          className=""
          urlPath={routes.forumCategory.path(Number(category_id))}
          total={totalBoards}
          start={1}
          end={Math.ceil(totalBoards / LIMIT)}
          current={page}
        />
      )}
      {spFlag && isLoading && currentPage > 1 && (
        <ForumLoadingPage
          size={18}
          className={STForumCategory.boardListLoading}
        />
      )}
      {spFlag && <div id="scrollTarget" style={{ height: "1px" }}></div>}
    </div>
  );
};

const ForumCategory = () => {
  const params = useParams<{ category_id?: string }>();
  const { category_id = 0 } = params ?? {};
  const [categoryData, setCategoryData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const history = useHistory();
  const onNavigateToCreateBoardButton = () => {
    history.push(
      `/message-board/board/create?board_category_id=${category_id}`
    );
  };

  useEffect(() => {
    const getForumCategoryDetail = async () => {
      try {
        const response = await fetchForumCategoriesDetail(Number(category_id));
        if (!response?.isSuccess || !response?.result) {
          history.push(routes.error.path);
        } else {
          const result = response?.result;
          setCategoryData(result);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    getForumCategoryDetail();
  }, [category_id, history]);

  if (isLoading) {
    return <ForumLoadingPage className={STForumCategory.container} />;
  }

  return (
    <div className={STForumCategory.container}>
      <div className={STForumCategory.navigateButtonGroup}>
        <Link
          to="/message-board/categories"
          className={STForumCategory.navigateToForumCategoriesButton}
        >
          <MdKeyboardDoubleArrowLeft />
          掲示板TOPに戻る
        </Link>
      </div>
      <ForumCategoryHeader
        id={categoryData.id}
        color={categoryData.color}
        title={categoryData.title}
        description={categoryData.description}
      />
      <NavigateToCreateBoardButton onClick={onNavigateToCreateBoardButton} />
      <ForumCategoryBoardList />
    </div>
  );
};

export default ForumCategory;
